import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
  paginatePatients,
  PatientPaginationPayload,
  DeletePatient,
} from "../../api/patient";
import { getClinicsByUser } from "../../api/clinic";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";

const defaultPayload: PatientPaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "created_on",
  direction: "desc",
  whereClause: [],
};

const PatientList: React.FC = () => {
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [payload, setPayload] =
    useState<PatientPaginationPayload>(defaultPayload);
  const lastFetchKeyRef = useRef<string>("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [search, setSearch] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const [searchType, setSearchType] = useState<"id" | "name" | "clinic">("name");

  // Clinics for the "Search by Clinic" dropdown.
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const fetchedClinicsRef = useRef(false);

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as "id" | "name" | "clinic";
    setSearchType(value);
    // The search term does not carry over between search types.
    setSearch("");
    setPayload((prev) => ({ ...prev, curPage: 1 }));
  };

  // Filter states
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    appointmentStatus: "",
    paymentStatus: "",
    sortDirection: "desc" as "asc" | "desc"
  });
  const [tempFilters, setTempFilters] = useState(filters);

  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const whereClause: any[] = [];

      // Add search filter
      if (search) {
        let key = "";

        if (searchType === "id") key = "uniqueCode";
        if (searchType === "name") key = "patientName";
        if (searchType === "clinic") key = "clinicName";

        whereClause.push({
          key,
          value: search,
          operator: "=",
        });
      }


      // Add appointment status filter
      if (filters.appointmentStatus) {
        whereClause.push({
          key: "appointmentStatus",
          value: filters.appointmentStatus,
          operator: "=",
        });
      }

      // Add payment status filter
      if (filters.paymentStatus) {
        whereClause.push({
          key: "paymentStatus",
          value: filters.paymentStatus,
          operator: "=",
        });
      }

      const body: PatientPaginationPayload = {
        ...payload,
        direction: filters.sortDirection,
        whereClause,
      };

      const res = await paginatePatients(body);
      const data = res?.data?.data || res?.data || {};
      setPatients(
        Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []
      );
      setTotal(data.total || res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load patients");
      setPatients([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  const applyFilters = () => {
    setFilters(tempFilters);
    setPayload(prev => ({ ...prev, curPage: 1 }));
    setIsFilterOpen(false);
  };

  // Reset filters
  const resetFilters = () => {
    const resetState = {
      appointmentStatus: "",
      paymentStatus: "",
      sortDirection: "desc" as "asc" | "desc"
    };
    setTempFilters(resetState);
    setFilters(resetState);
    setPayload(prev => ({ ...prev, curPage: 1 }));
    setIsFilterOpen(false);
  };

  // Load the doctor's clinics once, for the "Search by Clinic" dropdown.
  useEffect(() => {
    if (fetchedClinicsRef.current) return;
    fetchedClinicsRef.current = true;
    (async () => {
      try {
        const res = await getClinicsByUser();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c: any) => ({
            id: c.id || c._id || c.clinicId || c.value || "",
            name: c.name || c.clinicName || c.label || "",
          }))
          .filter((c: { id: string; name: string }) => c.id && c.name);
        setClinics(normalized);
      } catch {
        setClinics([]);
      }
    })();
  }, []);

  useEffect(() => {
    const key = JSON.stringify({
      p: payload.curPage,
      pp: payload.perPage,
      s: payload.sortBy,
      d: filters.sortDirection,
      q: search,
      as: filters.appointmentStatus,
      ps: filters.paymentStatus,
    });
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload.curPage, payload.perPage, payload.sortBy, filters, search]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');

      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };

  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const handleConfirm = async (id: string) => {
    try {
      setLoading(true);
      await DeletePatient(id);
      setShowConfirm(false);
      setShowSuccess(true);
      fetchPatients();
      // Do not close parent here; success modal will handle close via its onClose
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTreatment = (patient: any) => {
    const patientId = patient.id || patient._id || "";
    const clinicId = patient.clinicId || patient.clinic?.id || "";

    if (!patientId) {
      toast.error("Patient ID not found");
      return;
    }

    if (!clinicId) {
      toast.error("Clinic ID not found for this patient");
      return;
    }

    // Navigate to treatment form with query parameters to pre-fill clinic and patient
    const queryParams = new URLSearchParams({
      patientId,
      clinicId
    });

    navigate(`/treatment?${queryParams.toString()}`);
  };
  const getSearchPlaceholder = () => {
    switch (searchType) {
      case "id":
        return "Search patient by Id...";
      case "clinic":
        return "Search patient by clinic...";
      case "name":
      default:
        return "Search patient by name...";
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              {/* Header Row */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Patients
                </h2>
                <button
                  className="px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                  onClick={() => navigate("/patients/create")}
                >
                  + New Patient
                </button>
              </div>

              {/* Controls */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                <div className="text-sm text-gray-600">
                  Total: <span className="font-medium">{total}</span>
                </div>

                <div className="flex-1 flex flex-col md:flex-row md:items-center md:justify-end gap-3">
                  <div className="w-full md:w-48">
                    <select
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      className="w-full mt-1 p-2 border rounded-lg"
                    >
                      <option value="id">Search by Id</option>
                      <option value="name">Search by Name</option>
                      <option value="clinic">Search by Clinic</option>
                    </select>
                  </div>
                  {/* Search and Filter */}
                  <div className="flex items-center gap-3">
                    <div className="w-full md:w-64">
                      {searchType === "clinic" ? (
                        <select
                          value={search}
                          onChange={(e) => {
                            setSearch(e.target.value);
                            // reset to first page when the clinic changes
                            setPayload((prev) => ({ ...prev, curPage: 1 }));
                          }}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">All Clinics</option>
                          {clinics.map((c) => (
                            <option key={c.id} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => {
                            setSearch(e.target.value);
                            // reset to first page when search changes
                            setPayload((prev) => ({ ...prev, curPage: 1 }));
                          }}
                          placeholder={getSearchPlaceholder()}
                          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>

                    {/* Filter Button */}
                    <button
                      onClick={() => {
                        setTempFilters(filters);
                        setIsFilterOpen(true);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                      </svg>
                      Filter
                    </button>
                  </div>

                  {/* Rows per page */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Rows:</span>
                    <select
                      className="border rounded-lg p-1 select-plain"
                      value={payload.perPage}
                      onChange={changePerPage}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Patient
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Patient Id
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Clinic
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 hidden sm:table-cell">
                        Email
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 hidden sm:table-cell">
                        Phone
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {patients.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={6}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      patients.map((p: any) => (
                        <tr key={p.id || p._id} className="border-t">
                          <td className="px-4 py-3 text-sm text-gray-800">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200">
                                <img
                                  src={
                                    p.profilePictureUrlpath ||
                                    p.profilePictureUrl ||
                                    p.image ||
                                    p.imageUrl ||
                                    p.photoUrl ||
                                    "/images/dummy-profile.webp"
                                  }
                                  alt={
                                    p.name ||
                                    `${p.firstName || ""} ${p.lastName || ""}`.trim() ||
                                    "Patient"
                                  }
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex flex-col">
                                <span className="font-medium text-gray-900">
                                  {p.name ||
                                    `${p.firstName || ""} ${p.lastName || ""}`.trim()}
                                </span>
                                <span className="text-xs text-gray-500 sm:hidden mt-0.5">
                                  {p.contact || p.mobile || "—"}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                            {p.uniqueCode || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                            {p.clinic?.name || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                            {p.email || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                            {p.contact || p.mobile || "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div
                              className="relative"
                              ref={(el) => {
                                const patientId = p.id || p._id;
                                if (openMenuId === patientId) {
                                  (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                }
                              }}
                            >
                              <button
                                ref={(el) => {
                                  if (el) {
                                    buttonRefs.current[p.id || p._id || ""] = el;
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                                onClick={(e) => {
                                  const patientId = p.id || p._id || "";
                                  if (openMenuId === patientId) {
                                    setOpenMenuId(null);
                                    setDropdownPosition(null);
                                  } else {
                                    const button = e.currentTarget;
                                    const rect = button.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + window.scrollY + 4,
                                      left: rect.right + 4,
                                    });
                                    setOpenMenuId(patientId);
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="12" cy="19" r="2" />
                                </svg>
                              </button>
                              {openMenuId === (p.id || p._id) && dropdownPosition && createPortal(
                                <div
                                  className="dropdown-menu-container fixed w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                  }}
                                >
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      navigate(
                                        `/patients/create/${p.id || p._id || ""}`
                                      );
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Edit
                                  </button>

                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      handleCreateTreatment(p);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Create Treatment
                                  </button>

                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      setSelectedPatientId(p.id || p._id || "");
                                      setShowConfirm(true);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Delete
                                  </button>

                                </div>,
                                document.body
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <hr />
              {/* Pagination */}
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {payload.curPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(1)}
                    disabled={payload.curPage === 1}
                  >
                    « First
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage - 1)}
                    disabled={payload.curPage === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage + 1)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(totalPages)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Last »
                  </button>
                </div>
              </div>

              <ConfirmModal
                isOpen={showConfirm}
                title="Delete Patient"
                message="Are you sure you want to delete this patient?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={() => handleConfirm(selectedPatientId)}
                onCancel={() => setShowConfirm(false)}
                loading={loading}
              />
              <SuccessModal
                isOpen={showSuccess}
                title="Patient Deleted"
                message="The patient has been deleted successfully."
                onClose={() => setShowSuccess(false)}
              />

              {/* Filter Modal */}
              {isFilterOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                  <div
                    className="absolute inset-0 bg-black/50"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                      <h3 className="text-xl font-semibold text-teal-600">Filter</h3>
                      <button
                        onClick={() => setIsFilterOpen(false)}
                        className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div className="p-6 space-y-6">
                      {/* Sort by */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Sort by</label>
                        <select
                          value={tempFilters.sortDirection}
                          onChange={(e) => setTempFilters(prev => ({ ...prev, sortDirection: e.target.value as "asc" | "desc" }))}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="desc">Newest first</option>
                          <option value="asc">Oldest first</option>
                        </select>
                      </div>

                      {/* Appointment Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Appointment status</label>
                        <div className="grid grid-cols-3 gap-3">
                          <button
                            onClick={() => setTempFilters(prev => ({
                              ...prev,
                              appointmentStatus: prev.appointmentStatus === "COMPLETED" ? "" : "COMPLETED"
                            }))}
                            className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${tempFilters.appointmentStatus === "COMPLETED"
                              ? "border-teal-500 bg-teal-50 text-teal-700"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                          >
                            Completed
                          </button>
                          <button
                            onClick={() => setTempFilters(prev => ({
                              ...prev,
                              appointmentStatus: prev.appointmentStatus === "SCHEDULED" ? "" : "SCHEDULED"
                            }))}
                            className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${tempFilters.appointmentStatus === "SCHEDULED"
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                          >
                            Upcoming
                          </button>
                          <button
                            onClick={() => setTempFilters(prev => ({
                              ...prev,
                              appointmentStatus: prev.appointmentStatus === "CANCELLED" ? "" : "CANCELLED"
                            }))}
                            className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${tempFilters.appointmentStatus === "CANCELLED"
                              ? "border-red-500 bg-red-50 text-red-700"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                          >
                            Missed
                          </button>
                        </div>
                      </div>

                      {/* Payment Status */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Payment status</label>
                        <div className="grid grid-cols-3 gap-3">
                          <button
                            onClick={() => setTempFilters(prev => ({
                              ...prev,
                              paymentStatus: prev.paymentStatus === "Paid" ? "" : "Paid"
                            }))}
                            className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${tempFilters.paymentStatus === "Paid"
                              ? "border-teal-500 bg-teal-50 text-teal-700"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                          >
                            Paid
                          </button>
                          <button
                            onClick={() => setTempFilters(prev => ({
                              ...prev,
                              paymentStatus: prev.paymentStatus === "Due" ? "" : "Due"
                            }))}
                            className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${tempFilters.paymentStatus === "Due"
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                          >
                            Due
                          </button>
                          <button
                            onClick={() => setTempFilters(prev => ({
                              ...prev,
                              paymentStatus: prev.paymentStatus === "Partial" ? "" : "Partial"
                            }))}
                            className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${tempFilters.paymentStatus === "Partial"
                              ? "border-red-500 bg-red-50 text-red-700"
                              : "border-gray-200 text-gray-600 hover:border-gray-300"
                              }`}
                          >
                            Partial
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Modal Footer */}
                    <div className="flex items-center justify-between p-6 border-t border-gray-200">
                      <button
                        onClick={resetFilters}
                        className="p-3 rounded-full border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      <button
                        onClick={applyFilters}
                        className="flex-1 ml-4 py-3 px-6 bg-teal-600 text-white rounded-full font-medium hover:bg-teal-700 transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default PatientList;
